<template>
    <transition name="cz3__fade">
        <div
            v-if="showModal"
            class="z-50 fixed top-0 left-0 w-full h-full flex flex-col justify-center items-center bg-black bg-opacity-30"
        >
            <div class="shadow-md rounded max-h-full overflow-auto w-full sm:w-11/12 max-w-5xl p-4 md:p-9 bg-white text-ink">
                <div class="mb-6 flex items-center justify-between">
                    <h1 class="text-2xl font-bold tracking-wide">
                        {{ heading }}
                    </h1>

                    <button
                        @click="emitClose"
                        type="button"
                        aria-label="close modal"
                    >
                        <img
                            src="../assets/images/icons/close.svg"
                            width="20"
                            height="20"
                            alt="X"
                        >
                    </button>
                </div>

                <div v-if="!emailSent">
                    <div class="mb-6 flex flex-col gap-6">
                        <div class="flex flex-col sm:flex-row sm:gap-4">
                            <label class="sm:min-w-[210px] text-base font-gotham font-medium">
                                <span class="text-red">*</span>
                                {{ $t('YOUR_NAME') }}:
                            </label>

                            <base-input
                                input-name="senderName"
                                :input-value="senderName"
                                @change="senderName = $event.target.value"
                                :error-message="senderNameError"
                                :show-error-message="senderNameError !== ''"
                                class="w-full sm:w-72"
                            />
                        </div>

                        <div class="flex flex-col sm:flex-row sm:gap-4">
                            <label class="sm:min-w-[210px] text-base font-gotham font-medium">
                                <span class="text-red">*</span>
                                {{ $t('YOUR_EMAIL') }}:
                            </label>

                            <base-input
                                input-type="email"
                                input-name="senderEmail"
                                :input-value="senderEmail"
                                @change="senderEmail = $event.target.value"
                                :error-message="senderEmailError"
                                :show-error-message="senderEmailError !== ''"
                                class="w-full sm:w-72"
                            />
                        </div>

                        <div class="flex flex-col sm:flex-row sm:gap-4">
                            <label class="sm:min-w-[210px] text-base font-gotham font-medium">
                                <span class="text-red">*</span>
                                {{ $t('RECIPIENT_NAME') }}:
                            </label>

                            <base-input
                                input-name="recipientName"
                                :input-value="recipientName"
                                @change="recipientName = $event.target.value"
                                :error-message="recipientNameError"
                                :show-error-message="recipientNameError !== ''"
                                class="w-full sm:w-72"
                            />
                        </div>

                        <div class="flex flex-col sm:flex-row sm:gap-4">
                            <label class="sm:min-w-[210px] text-base font-gotham font-medium">
                                <span class="text-red">*</span>
                                {{ $t('RECIPIENT_EMAIL') }}:
                            </label>

                            <base-input
                                input-type="email"
                                input-name="recipientEmail"
                                :input-value="recipientEmail"
                                @change="recipientEmail = $event.target.value"
                                :error-message="recipientEmailError"
                                :show-error-message="recipientEmailError !== ''"
                                class="w-full sm:w-72"
                            />
                        </div>

                        <div class="flex flex-col sm:flex-row sm:gap-4">
                            <label class="sm:min-w-[210px] text-base font-gotham font-medium">
                                {{ $t('MESSAGE') }}:
                            </label>

                            <div class="w-full">
                                <textarea
                                    name="message"
                                    v-model="message"
                                    :class="messageError !== '' ? 'border-red': 'border-black'"
                                    class="rounded-sm border w-full h-24 px-2 py-3 bg-white font-sans"
                                >
                                </textarea>

                                <p
                                    v-if="messageError !== ''"
                                    class="mt-2 text-xs text-red"
                                >
                                    {{ messageError }}
                                </p>
                            </div>
                        </div>

                        <div class="flex flex-col sm:flex-row sm:gap-4">
                            <span class="sm:min-w-[210px]">
                            </span>

                            <base-checkbox checkbox-id="sendCopy" v-model="sendCopy">
                                {{ $t('SEND_COPY') }}
                            </base-checkbox>
                        </div>
                    </div>

                    <div class="flex justify-end gap-4">
                        <base-button-flat
                            @click="emitClose"
                            variant="secondary"
                            class="w-36 h-10 capitalize"
                        >
                            {{ $t('CANCEL') }}
                        </base-button-flat>

                        <base-button-flat
                            :is-disabled="emailSending"
                            @click="validateFields"
                            class="w-36 h-10 capitalize"
                        >
                            <loading-spinner
                                v-if="emailSending"
                                class="h-6 w-6 text-white"
                            />

                            <span v-else>
                                {{ $t('SUBMIT') }}
                            </span>
                        </base-button-flat>
                    </div>
                </div>

                <div v-else>
                    <p class="mb-6">
                        {{ $t('EMAIL_SENT') }}
                    </p>

                    <div class="flex justify-end">
                        <base-button-flat
                            @click="emitClose"
                            class="w-28 h-10 capitalize"
                        >
                            {{ $t('OK') }}
                        </base-button-flat>
                    </div>
                </div>
            </div>
        </div>
    </transition>
</template>

<script>
    import allStores from '../stores/all';

    import BaseInput from './BaseInput.vue';
    import BaseCheckbox from './BaseCheckbox.vue';
    import BaseButtonFlat from './BaseButtonFlat.vue';
    import LoadingSpinner from './LoadingSpinner.vue';

    export default {
        mixins: [allStores],

        components: {
            BaseInput,
            BaseCheckbox,
            BaseButtonFlat,
            LoadingSpinner,
        },

        props: {
            showModal: {
                type: Boolean,
                default: false,
            },
        },

        data() {
            return {
                senderName: '',
                senderEmail: '',
                recipientName: '',
                recipientEmail: '',
                message: '',
                senderNameError: '',
                senderEmailError: '',
                recipientNameError: '',
                recipientEmailError: '',
                messageError: '',
                emailSent: false,
                emailSending: false,
                sendCopy: false,
            };
        },

        computed: {
            heading() {
                return this.emailSent ? `${this.$t('EMAIL_SUMMARY')}` : `${this.$t('EMAIL')}`;
            },

            viewController() {
                return this.customizerStore.viewController;
            },
        },

        methods: {
            validateFields() {
                this.senderNameError = this.senderName ? '' : `${this.$t('REQUIRED')}`;
                this.senderEmailError = this.senderEmail ? this.validateEmail(this.senderEmail) : `${this.$t('REQUIRED')}`;
                this.recipientNameError = this.recipientName ? '' : `${this.$t('REQUIRED')}`;
                this.recipientEmailError = this.recipientEmail ? this.validateEmail(this.recipientEmail) : `${this.$t('REQUIRED')}`;
                this.checkMessageForProfanity();

                if (!this.senderNameError && !this.senderEmailError && !this.recipientNameError && !this.recipientEmailError && !this.messageError) {
                    this.submitEmail();
                }
            },

            validateEmail(emailAddress) {
                let errorMessage = `${this.$t('EMAIL_INVALID')}`;

                // eslint-disable-next-line
                if (/^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/.test(emailAddress)) {
                    errorMessage = '';
                }

                return errorMessage;
            },

            checkMessageForProfanity() {
                if (this.message !== '') {
                    this.viewController.reviewText(this.message).then((result) => {
                        if (result.hasProfanity) {
                            this.messageError = `${this.$t('INVALID_ERROR')}`;
                        } else {
                            this.messageError = '';
                        }
                    }).catch(() => {});
                } else {
                    this.messageError = '';
                }
            },

            submitEmail() {
                this.emailSending = true;

                this.customizerStore.saveAndShare({
                    from: this.senderName,
                    fromEmail: this.senderEmail,
                    to: this.recipientName,
                    toEmail: this.recipientEmail,
                    message: this.message,
                    sendCopy: this.sendCopy,
                }).then(() => {
                    this.emailSending = false;
                    this.emailSent = true;
                });
            },

            reset() {
                this.senderName = '';
                this.senderEmail = '';
                this.recipientName = '';
                this.recipientEmail = '';
                this.message = '';
                this.senderNameError = '';
                this.senderEmailError = '';
                this.recipientNameError = '';
                this.recipientEmailError = '';
                this.messageError = '';
                this.emailSent = false;
                this.emailSending = false;

                this.emitClose();
            },

            emitClose() {
                this.$emit('modalClose');

                if (this.emailSent) {
                    this.reset();
                }
            },
        },
    };
</script>
